import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { storesAPI, productsAPI, mediaAPI } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { BottomNav } from './MarketplaceHome';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShoppingCart,
  MapPin,
  Phone,
  MessageCircle,
  Star,
  Plus,
  Play,
  ChevronRight,
  Image as ImageIcon
} from 'lucide-react';

export const StoreView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, itemCount } = useCart();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [storeRes, productsRes, mediaRes] = await Promise.all([
        storesAPI.get(id),
        productsAPI.list({ store_id: id }),
        mediaAPI.list(id)
      ]);
      setStore(storeRes.data);
      setProducts(productsRes.data);
      setMedia(mediaRes.data);
    } catch (error) {
      toast.error('Loja não encontrada');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} adicionado!`);
  };

  const openWhatsApp = () => {
    const phone = store?.whatsapp?.replace(/\D/g, '');
    if (phone) {
      window.open(`https://wa.me/55${phone}?text=Olá! Vi sua loja no DESTACK VENDAS MAIS e gostaria de mais informações.`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!store) return null;

  const videos = media.filter(m => m.resource_type === 'video');
  const images = media.filter(m => m.resource_type === 'image');

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="store-view-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="font-semibold text-gray-900 truncate flex-1 mx-4">{store.name}</h1>
          <button onClick={() => navigate('/carrinho')} className="p-2 -mr-2 relative">
            <ShoppingCart className="w-6 h-6 text-gray-700" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Store Banner/Logo */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center overflow-hidden">
            {store.logo_url ? (
              <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-blue-600 text-3xl font-bold">{store.name.charAt(0)}</span>
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold">{store.name}</h2>
            <p className="text-blue-200 text-sm flex items-center gap-1 mt-1">
              <MapPin className="w-4 h-4" />
              {store.city}/{store.state}
            </p>
            {store.rating > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span className="text-sm">{store.rating.toFixed(1)}</span>
              </div>
            )}
          </div>
        </div>
        
        {store.description && (
          <p className="mt-4 text-blue-100 text-sm">{store.description}</p>
        )}

        <div className="mt-4">
          <Link
            to={`/loja/${store.id}/institucional`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white text-sm font-semibold transition-colors"
            data-testid="institutional-page-link"
          >
            Página institucional completa
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="flex gap-3 mt-4">
          <button
            onClick={openWhatsApp}
            className="flex-1 py-3 bg-green-500 rounded-xl font-semibold flex items-center justify-center gap-2"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp
          </button>
          <a
            href={`tel:${store.phone}`}
            className="flex-1 py-3 bg-white/20 rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Ligar
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-14 z-40">
        <div className="flex">
          <button
            onClick={() => setActiveTab('products')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'products' 
                ? 'text-blue-600 border-blue-600' 
                : 'text-gray-500 border-transparent'
            }`}
          >
            Produtos ({products.length})
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'photos' 
                ? 'text-blue-600 border-blue-600' 
                : 'text-gray-500 border-transparent'
            }`}
          >
            Fotos ({images.length})
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'videos' 
                ? 'text-blue-600 border-blue-600' 
                : 'text-gray-500 border-transparent'
            }`}
          >
            Vídeos ({videos.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'products' && (
          products.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3" />
              <p>Nenhum produto cadastrado</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/produto/${product.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm"
                  data-testid={`product-${product.id}`}
                >
                  <div className="relative">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/200x200?text=Produto'}
                      alt={product.name}
                      className="w-full aspect-square object-cover"
                    />
                    {product.promo_price && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                        OFERTA
                      </span>
                    )}
                    <button
                      onClick={(e) => handleAddToCart(e, product)}
                      className="absolute bottom-2 right-2 w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 text-sm truncate">{product.name}</h3>
                    <div className="mt-1">
                      {product.promo_price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 line-through text-xs">{formatPrice(product.price)}</span>
                          <span className="font-bold text-green-600">{formatPrice(product.promo_price)}</span>
                        </div>
                      ) : (
                        <span className="font-bold text-gray-900">{formatPrice(product.price)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}

        {activeTab === 'photos' && (
          images.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-3" />
              <p>Nenhuma foto cadastrada</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {images.map((item) => (
                <div key={item.id} className="aspect-square rounded-lg overflow-hidden">
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === 'videos' && (
          videos.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Play className="w-12 h-12 mx-auto mb-3" />
              <p>Nenhum vídeo cadastrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {videos.map((item) => (
                <div key={item.id} className="rounded-xl overflow-hidden">
                  <video src={item.url} controls className="w-full" />
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default StoreView;
